import { useState } from 'react';
import PropTypes from 'prop-types';

const ROLE_LABELS = {
	consumer: 'Consumer',
	provider: 'Provider',
	both: 'Both',
};

// Stable, unique panel ids so multiple instances never collide on `id`
// (React 17 has no useId). The counter only ever increments, and each
// instance keeps its first value for its lifetime via the lazy initializer.
let instanceCount = 0;

function OrgList({ orgs }) {
	if (orgs.length === 0) {
		return (
			<p className="dsv-affected-orgs__empty" data-testid="affected-orgs-empty">
				No organisations affected.
			</p>
		);
	}

	return (
		<table className="dsv-affected-orgs__table" data-testid="affected-orgs-table">
			<thead>
				<tr>
					<th scope="col">Organisation</th>
					<th scope="col">Role</th>
					<th scope="col">Type</th>
					<th scope="col">Industry</th>
				</tr>
			</thead>
			<tbody>
				{orgs.map((org) => (
					<tr key={org.orgId} data-testid={`affected-orgs-row-${org.orgId}`}>
						<td>{org.name}</td>
						<td>{ROLE_LABELS[org.role] || org.role}</td>
						<td>{org.orgType || '—'}</td>
						<td>{org.industry || '—'}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

OrgList.propTypes = {
	orgs: PropTypes.array.isRequired,
};

function AffectedOrganisations({ organizations, collapsible }) {
	const orgs = organizations || [];
	const [expanded, setExpanded] = useState(false);
	const [panelId] = useState(() => `affected-orgs-panel-${(instanceCount += 1)}`);

	// Always-open variant (e.g. the update-schema Impact Analysis tab): the
	// table / empty state renders inline with no toggle control.
	if (!collapsible) {
		return (
			<div className="dsv-affected-orgs">
				<OrgList orgs={orgs} />
			</div>
		);
	}

	// Collapsible variant (DSV detail page): a "View affected organisations (N)"
	// toggle reveals the list; collapsed by default.
	return (
		<div className="dsv-affected-orgs">
			<button
				type="button"
				className="dsv-affected-orgs__toggle"
				data-testid="affected-orgs-toggle"
				aria-expanded={expanded}
				aria-controls={panelId}
				onClick={() => setExpanded((value) => !value)}
			>
				{expanded ? 'Hide affected organisations' : `View affected organisations (${orgs.length})`}
			</button>

			{expanded && (
				<div id={panelId} className="dsv-affected-orgs__panel">
					<OrgList orgs={orgs} />
				</div>
			)}
		</div>
	);
}

AffectedOrganisations.propTypes = {
	organizations: PropTypes.arrayOf(
		PropTypes.shape({
			orgId: PropTypes.string,
			name: PropTypes.string,
			orgType: PropTypes.string,
			industry: PropTypes.string,
			role: PropTypes.string,
		})
	),
	collapsible: PropTypes.bool,
};

AffectedOrganisations.defaultProps = {
	organizations: [],
	collapsible: false,
};

export default AffectedOrganisations;
